import * as React from "react";
import { PanelLeftIcon } from "lucide-react";

import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { useTranslation } from "react-i18next";
import { SidebarContext, SidebarContextProps } from "./Sidebar";
import { useFilterEnabled } from "../FilterEnabledProvider";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";

type MultiSidebarContextProps = Record<string, SidebarContextProps>;
export type OpenType = Record<string, boolean>;

const MultiSidebarContext =
  React.createContext<MultiSidebarContextProps | null>(null);

function useMultiSidebar(id: string) {
  const context = React.useContext(MultiSidebarContext);
  if (!context) {
    throw new Error(
      "useMultiSidebar must be used within a MultiSidebarProvider.",
    );
  }

  if (!context[id]) {
    throw new Error("useMultiSidebar must be provided a valid sidebar id.");
  }

  return context[id];
}

function MultiSidebarProvider({
  sidebarProps,
  className,
  style,
  children,
  collapseOthersOnExpand = true,
  ...props
}: React.ComponentProps<"div"> & {
  sidebarProps: Record<
    string,
    {
      defaultOpen?: boolean;
      open?: boolean;
      onOpenChange?: (open: boolean) => void;
      keyShortcut?: string;
      sidebarWidth?: string;
      sidebarWidthMobile?: string;
      sidebarWidthIcon?: string;
    }
  >;
  collapseOthersOnExpand?: boolean;
}) {
  const isMobile = useIsMobile();
  const sidebarIds = Object.keys(sidebarProps);

  const filterEnabledState = useFilterEnabled();

  type OpenMobileType = Record<string, boolean>;
  const openMobileDefaults = sidebarIds.reduce<OpenMobileType>((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});
  const [openMobile, setOpenMobile] =
    React.useState<OpenMobileType>(openMobileDefaults);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState<OpenType>(
    sidebarIds.reduce<OpenType>((acc, key) => {
      acc[key] = sidebarProps[key].defaultOpen ?? true;
      return acc;
    }, {}),
  );

  const open: OpenType = React.useMemo(
    () =>
      sidebarIds.reduce<OpenType>((acc, key) => {
        acc[key] = sidebarProps[key].open || _open[key];
        return acc;
      }, {}),
    [sidebarIds, sidebarProps, _open],
  );

  const setOpen = React.useCallback(
    (id: string, value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open[id]) : value;
      if (sidebarProps[id].onOpenChange) {
        sidebarProps[id].onOpenChange(openState);
      } else {
        _setOpen((prev) => {
          let open: OpenType = {};
          if (collapseOthersOnExpand) {
            const newOpen = Object.keys(prev).reduce<OpenType>((acc, key) => {
              acc[key] = false;
              return acc;
            }, {});
            newOpen[id] = openState;
            open = newOpen;
          } else open = { ...prev, [id]: openState };
          return openStatesEqual(open, prev) ? prev : open;
        });
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}:${id}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [open, sidebarProps, collapseOthersOnExpand],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(
    (id: string) => {
      return isMobile
        ? setOpenMobile((open) =>
            Object.keys(open).reduce<OpenType>((acc, cur) => {
              acc[cur] = cur === id;
              return acc;
            }, {}),
          )
        : setOpen(id, (open) => !open);
    },
    [isMobile, setOpen, setOpenMobile],
  );

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      for (const [id, { keyShortcut }] of Object.entries(sidebarProps)) {
        if (event.key === keyShortcut && (event.metaKey || event.ctrlKey)) {
          if (
            id === "filter" &&
            filterEnabledState &&
            !filterEnabledState.isEnabled
          ) {
            continue;
          }

          event.preventDefault();
          toggleSidebar(id);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar, sidebarProps, filterEnabledState]);

  React.useEffect(() => {
    if (filterEnabledState && !filterEnabledState.isEnabled && open.filter) {
      setOpen("filter", false);
    }
  }, [filterEnabledState, open.filter, setOpen]);

  const contextValue = React.useMemo<MultiSidebarContextProps>(
    () =>
      sidebarIds.reduce<MultiSidebarContextProps>((acc, cur) => {
        acc[cur] = {
          state: open[cur] ? "expanded" : "collapsed",
          open: open[cur],
          setOpen: (val) => setOpen(cur, val),
          isMobile,
          openMobile: openMobile[cur],
          setOpenMobile: (val) => {
            setOpenMobile((prev) => ({ ...prev, [cur]: val }));
          },
          toggleSidebar: () => {
            toggleSidebar(cur);
          },
          sidebarWidth: sidebarProps[cur].sidebarWidth || SIDEBAR_WIDTH,
          sidebarWidthMobile:
            sidebarProps[cur].sidebarWidthMobile || SIDEBAR_WIDTH_MOBILE,
          sidebarWidthIcon:
            sidebarProps[cur].sidebarWidthIcon || SIDEBAR_WIDTH_ICON,
        };
        return acc;
      }, {}),
    [
      sidebarIds,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      sidebarProps,
    ],
  );

  return (
    <MultiSidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </MultiSidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  sidebarId,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
  sidebarId: string;
}) {
  const sidebarContext = useMultiSidebar(sidebarId);
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile,
    sidebarWidth,
    sidebarWidthMobile,
    sidebarWidthIcon,
  } = sidebarContext;
  const { t } = useTranslation();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        style={
          {
            "--sidebar-width": sidebarWidth,
          } as React.CSSProperties
        }
        className={cn(
          "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <SidebarContext.Provider value={sidebarContext}>
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-slot="sidebar"
            data-mobile="true"
            data-testid={`${sidebarId}-mobile`}
            className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
            style={
              {
                "--sidebar-width": sidebarWidthMobile,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t("nav.sidebar")}</SheetTitle>
              <SheetDescription>{t("nav.displaySidebar")}</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      </SidebarContext.Provider>
    );
  }

  return (
    <SidebarContext.Provider value={sidebarContext}>
      <div
        className="group peer hidden text-sidebar-foreground md:block"
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
        data-slot="sidebar"
        data-testid={sidebarId}
        style={
          {
            "--sidebar-width": sidebarWidth,
            "--sidebar-width-mobile": sidebarWidthMobile,
            "--sidebar-width-icon": sidebarWidthIcon,
          } as React.CSSProperties
        }
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          data-slot="sidebar-gap"
          className={cn(
            "relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
              : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
          )}
        />
        <div
          data-slot="sidebar-container"
          className={cn(
            "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
              : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
            className,
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            data-slot="sidebar-inner"
            className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm"
          >
            {children}
          </div>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}

function SidebarTrigger({
  className,
  onClick,
  sidebarId,
  ...props
}: React.ComponentProps<typeof Button> & { sidebarId: string }) {
  const { t } = useTranslation();
  const { toggleSidebar } = useMultiSidebar(sidebarId);

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn("size-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">{t("nav.toggleSidebar")}</span>
    </Button>
  );
}

export const openStatesEqual = (open1: OpenType, open2: OpenType): boolean => {
  const keys1 = Object.keys(open1);
  if (keys1.length !== Object.keys(open2).length) {
    return false;
  }
  return keys1.reduce((acc, cur) => open1[cur] === open2[cur] && acc, true);
};

export { Sidebar, MultiSidebarProvider, SidebarTrigger, useMultiSidebar };
